import { create } from '@signals/app-shell/shims/zustand';
import { PathfinderStudent, PathfinderDataPoint, PathfinderRoute } from '../services/pathfinderApi';
import { ItinerarySubmission } from '@signals/app-shell/services/itineraryApi';
import { DrawCircle } from '../components/pathfinder/AreaSearch';

type SetterArg<T> = T | ((prev: T) => T);
type CourseDateRange = { min_date: string; max_date: string };

interface PathfinderState {
  // Session flag
  hasData: boolean;

  // User selections
  selectedCourse: string | undefined;
  selectedStudents: string[];
  dateRange: { start: Date | null; end: Date | null };
  customDeviceColors: Record<string, string>;
  defaultRadius: number;

  // Reference data
  availableCourses: { id: string; title: string }[];
  coursesData: Record<string, string[]>;
  availableStudents: PathfinderStudent[];
  courseDateRanges: Record<string, CourseDateRange>;

  // Loaded data
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  dwellLocations: any[];
  coTravelerResults: any[];
  coTravelerVisibility: Record<string, boolean>;
  digitalPoLData: any[];
  itinerarySubmissions: ItinerarySubmission[];

  // Visible/filtered data
  visibleDataPoints: PathfinderDataPoint[];
  visibleRoutes: PathfinderRoute[];
  visibleDwells: any[];
  visibleAreaSearchDwells: any[];

  // Area search data
  areaSearchDataPoints: PathfinderDataPoint[];
  areaSearchRoutes: PathfinderRoute[];
  areaSearchSelectedDevices: Record<string, string[]>;
  areaSearchActualDevices: Record<string, any[]>;
  areaSearchDwellLocations: any[];
  areaSearchExpandedStudents: Record<string, boolean>;

  // Uploaded file data (kept separate from student/device data)
  uploadedDataPoints: PathfinderDataPoint[];
  uploadedRoutes: PathfinderRoute[];
  uploadedFileNames: string[];
  showUploadedData: boolean;
  uploadedFileVisibility: Record<string, boolean>;

  // Persistent UI preferences
  /** Stable right-panel tool id (e.g. area-search), not a filtered-tab index. */
  activeToolId: string;
  timeRange: { start: Date; end: Date } | null;
  drawCircles: DrawCircle[];
  activeAnnotations: any[];
  showDigitalPoLOnMap: boolean;
  showItineraryOnMap: boolean;

  // Setters — fields that use callback-style updates accept SetterArg<T>
  setHasData: (v: boolean) => void;
  setSelectedCourse: (v: SetterArg<string | undefined>) => void;
  setSelectedStudents: (v: SetterArg<string[]>) => void;
  setDateRange: (v: SetterArg<{ start: Date | null; end: Date | null }>) => void;
  setCustomDeviceColors: (v: SetterArg<Record<string, string>>) => void;
  setDefaultRadius: (v: number) => void;
  setAvailableCourses: (v: { id: string; title: string }[]) => void;
  setCoursesData: (v: Record<string, string[]>) => void;
  setAvailableStudents: (v: PathfinderStudent[]) => void;
  setCourseDateRanges: (v: Record<string, CourseDateRange>) => void;
  setDataPoints: (v: PathfinderDataPoint[]) => void;
  setRoutes: (v: PathfinderRoute[]) => void;
  setDwellLocations: (v: any[]) => void;
  setCoTravelerResults: (v: any[]) => void;
  setCoTravelerVisibility: (v: SetterArg<Record<string, boolean>>) => void;
  setDigitalPoLData: (v: any[]) => void;
  setItinerarySubmissions: (v: ItinerarySubmission[]) => void;
  setVisibleDataPoints: (v: PathfinderDataPoint[]) => void;
  setVisibleRoutes: (v: PathfinderRoute[]) => void;
  setVisibleDwells: (v: any[]) => void;
  setVisibleAreaSearchDwells: (v: any[]) => void;
  setAreaSearchDataPoints: (v: PathfinderDataPoint[]) => void;
  setAreaSearchRoutes: (v: PathfinderRoute[]) => void;
  setAreaSearchSelectedDevices: (v: Record<string, string[]>) => void;
  setAreaSearchActualDevices: (v: Record<string, any[]>) => void;
  setAreaSearchDwellLocations: (v: any[]) => void;
  setAreaSearchExpandedStudents: (v: Record<string, boolean>) => void;
  setUploadedDataPoints: (v: SetterArg<PathfinderDataPoint[]>) => void;
  setUploadedRoutes: (v: SetterArg<PathfinderRoute[]>) => void;
  setUploadedFileNames: (v: SetterArg<string[]>) => void;
  setShowUploadedData: (v: boolean) => void;
  setUploadedFileVisibility: (v: SetterArg<Record<string, boolean>>) => void;
  clearUploadedData: () => void;

  setActiveToolId: (v: string) => void;
  setTimeRange: (v: { start: Date; end: Date } | null) => void;
  setDrawCircles: (v: SetterArg<DrawCircle[]>) => void;
  setActiveAnnotations: (v: SetterArg<any[]>) => void;
  setShowDigitalPoLOnMap: (v: boolean) => void;
  setShowItineraryOnMap: (v: boolean) => void;

  resetAll: () => void;
}

const initialState = {
  hasData: false,
  selectedCourse: undefined as string | undefined,
  selectedStudents: [] as string[],
  dateRange: (() => {
    // Default to the last 1 hour. Twelve-hour defaults were dragging in
    // thousands of points on first load and slowing the sandbox tab open
    // (and warming up the user's laptop). Analysts can widen via the
    // picker chips (1h / 4h / 12h / 24h / 48h / 72h / 7d) on demand.
    const end = new Date();
    const start = new Date(end.getTime() - (1 * 60 * 60 * 1000));
    return { start, end } as { start: Date | null; end: Date | null };
  })(),
  customDeviceColors: {} as Record<string, string>,
  defaultRadius: 500,
  availableCourses: [] as { id: string; title: string }[],
  coursesData: {} as Record<string, string[]>,
  availableStudents: [] as PathfinderStudent[],
  courseDateRanges: {} as Record<string, CourseDateRange>,
  dataPoints: [] as PathfinderDataPoint[],
  routes: [] as PathfinderRoute[],
  dwellLocations: [] as any[],
  coTravelerResults: [] as any[],
  coTravelerVisibility: {} as Record<string, boolean>,
  digitalPoLData: [] as any[],
  itinerarySubmissions: [] as ItinerarySubmission[],
  visibleDataPoints: [] as PathfinderDataPoint[],
  visibleRoutes: [] as PathfinderRoute[],
  visibleDwells: [] as any[],
  visibleAreaSearchDwells: [] as any[],
  areaSearchDataPoints: [] as PathfinderDataPoint[],
  areaSearchRoutes: [] as PathfinderRoute[],
  areaSearchSelectedDevices: {} as Record<string, string[]>,
  areaSearchActualDevices: {} as Record<string, any[]>,
  areaSearchDwellLocations: [] as any[],
  areaSearchExpandedStudents: {} as Record<string, boolean>,
  uploadedDataPoints: [] as PathfinderDataPoint[],
  uploadedRoutes: [] as PathfinderRoute[],
  uploadedFileNames: [] as string[],
  showUploadedData: true,
  uploadedFileVisibility: {} as Record<string, boolean>,
  activeToolId: (() => {
    try {
      return sessionStorage.getItem('pathfinder.activeToolId') || 'interactive-charts';
    } catch {
      return 'interactive-charts';
    }
  })(),
  timeRange: null as { start: Date; end: Date } | null,
  drawCircles: [] as DrawCircle[],
  activeAnnotations: [] as any[],
  showDigitalPoLOnMap: false,
  showItineraryOnMap: false,
};

export const usePathfinderStore = create<PathfinderState>((set) => ({
  ...initialState,

  setHasData: (v) => set({ hasData: v }),
  setSelectedCourse: (v) => set((s) => ({ selectedCourse: typeof v === 'function' ? v(s.selectedCourse) : v })),
  setSelectedStudents: (v) => set((s) => ({ selectedStudents: typeof v === 'function' ? v(s.selectedStudents) : v })),
  setDateRange: (v) => set((s) => ({ dateRange: typeof v === 'function' ? v(s.dateRange) : v })),
  setCustomDeviceColors: (v) => set((s) => ({ customDeviceColors: typeof v === 'function' ? v(s.customDeviceColors) : v })),
  setDefaultRadius: (v) => set({ defaultRadius: v }),
  setAvailableCourses: (v) => set({ availableCourses: v }),
  setCoursesData: (v) => set({ coursesData: v }),
  setAvailableStudents: (v) => set({ availableStudents: v }),
  setCourseDateRanges: (v) => set({ courseDateRanges: v }),
  setDataPoints: (v) => set({ dataPoints: v, hasData: v.length > 0 }),
  setRoutes: (v) => set({ routes: v }),
  setDwellLocations: (v) => set({ dwellLocations: v }),
  setCoTravelerResults: (v) => set({ coTravelerResults: v }),
  setCoTravelerVisibility: (v) => set((s) => ({ coTravelerVisibility: typeof v === 'function' ? v(s.coTravelerVisibility) : v })),
  setDigitalPoLData: (v) => set({ digitalPoLData: v }),
  setItinerarySubmissions: (v) => set({ itinerarySubmissions: v }),
  setVisibleDataPoints: (v) => set({ visibleDataPoints: v }),
  setVisibleRoutes: (v) => set({ visibleRoutes: v }),
  setVisibleDwells: (v) => set({ visibleDwells: v }),
  setVisibleAreaSearchDwells: (v) => set({ visibleAreaSearchDwells: v }),
  setAreaSearchDataPoints: (v) => set({ areaSearchDataPoints: v }),
  setAreaSearchRoutes: (v) => set({ areaSearchRoutes: v }),
  setAreaSearchSelectedDevices: (v) => set({ areaSearchSelectedDevices: v }),
  setAreaSearchActualDevices: (v) => set({ areaSearchActualDevices: v }),
  setAreaSearchDwellLocations: (v) => set({ areaSearchDwellLocations: v }),
  setAreaSearchExpandedStudents: (v) => set({ areaSearchExpandedStudents: v }),
  setUploadedDataPoints: (v) => set((s) => ({ uploadedDataPoints: typeof v === 'function' ? v(s.uploadedDataPoints) : v })),
  setUploadedRoutes: (v) => set((s) => ({ uploadedRoutes: typeof v === 'function' ? v(s.uploadedRoutes) : v })),
  setUploadedFileNames: (v) => set((s) => ({ uploadedFileNames: typeof v === 'function' ? v(s.uploadedFileNames) : v })),
  setShowUploadedData: (v) => set({ showUploadedData: v }),
  setUploadedFileVisibility: (v) => set((s) => ({ uploadedFileVisibility: typeof v === 'function' ? v(s.uploadedFileVisibility) : v })),
  clearUploadedData: () => set({ uploadedDataPoints: [], uploadedRoutes: [], uploadedFileNames: [], showUploadedData: true, uploadedFileVisibility: {} }),

  setActiveToolId: (v) => {
    try {
      sessionStorage.setItem('pathfinder.activeToolId', v);
    } catch {
      /* ignore quota / private mode */
    }
    set({ activeToolId: v });
  },
  setTimeRange: (v) => set({ timeRange: v }),
  setDrawCircles: (v) => set((s) => ({ drawCircles: typeof v === 'function' ? v(s.drawCircles) : v })),
  setActiveAnnotations: (v) => set((s) => ({ activeAnnotations: typeof v === 'function' ? v(s.activeAnnotations) : v })),
  setShowDigitalPoLOnMap: (v) => set({ showDigitalPoLOnMap: v }),
  setShowItineraryOnMap: (v) => set({ showItineraryOnMap: v }),

  resetAll: () => set({
    ...initialState,
    dateRange: (() => {
      const end = new Date();
      const start = new Date(end.getTime() - (1 * 60 * 60 * 1000));
      return { start, end };
    })(),
  }),
}));
